import React, { useMemo, useCallback, useEffect, memo, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Stack,
  Chip,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DownloadIcon from '@mui/icons-material/Download';
import { AccordionGroup } from '@/shared/ui/Accordion';
import { CustomTable } from '@/shared/ui/Table';
import type { FormikProps, TargetGroup } from '@/entities/campaign';
import { DEFAULT_SCREEN_IMAGE } from '@/shared/constants';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { setImagePreviewModal, handleSnackbar } from '@/shared/ui';
import {
  calculateTargetAudience,
  hasFiltersSelected,
} from '@/shared/utils/audienceCalculation';
import { useAppSelector, useAppDispatch, RootState } from '@/app/store';
import { formatNumberCompact } from '@/shared/utils/format';
import { calculateAspectRatio } from '@/shared/utils';

interface ScreenSovProps {
  formik: FormikProps;
}

interface ScreenRow {
  id: string;
  name: string;
  price: number;
  address: object;
  resolution: string;
  orientation: string;
  numberOfSlots: number;
  slotTimeInSec: number;
  sov: number;
  numberOfBookedSlots: number;
  targetGroupTitle: string;
  targetGroupIndex: number;
  screenIndex: number;
}

const ScreenSov: React.FC<ScreenSovProps> = ({ formik }) => {
  const { targetGroups } = formik.values;
  const isInitialized = useRef(false);
  const dispatch = useAppDispatch();

  // Helper function to format loop time
  const formatLoopTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  };

  // Get targetGroupFilters from Redux
  const { targetGroupFilters } = useAppSelector(
    (state: RootState) => state.dooh.campaigns.campaignDetails
  );

  // Check if any filters are selected using shared utility
  const filtersSelected = hasFiltersSelected(targetGroupFilters);

  // Get all screens from all target groups for summary stats
  const allScreens = useMemo(() => {
    const screens: ScreenRow[] = [];

    targetGroups?.forEach((targetGroup: TargetGroup, tgIndex: number) => {
      targetGroup.selectedArea?.forEach((screen: any, screenIndex: number) => {
        const numberOfSlots = screen.numberOfSlots || 1;
        const defaultSov =
          numberOfSlots === 1 ? 100 : Math.floor(100 / numberOfSlots);
        const consumedSlots =
          screen.consumedSlots ||
          (screen.sov ? Math.ceil((screen.sov / 100) * numberOfSlots) : 1);

        screens.push({
          id: screen.id,
          name: screen.name,
          price: screen.price,
          address: screen.address || 'N/A',
          resolution: screen.resolution || 'N/A',
          orientation: screen.orientation || 'N/A',
          numberOfSlots,
          slotTimeInSec: screen.slotTimeInSec || 0,
          sov: screen.sov || defaultSov,
          numberOfBookedSlots: consumedSlots,
          targetGroupTitle: targetGroup.title || 'Unknown',
          targetGroupIndex: tgIndex,
          screenIndex,
        });
      });
    });

    return screens;
  }, [targetGroups]);

  const reduxSelectedScreens = useAppSelector(
    (state: RootState) => state.dooh.campaigns.campaignDetails.selectedScreens
  );

  const updateSelectedScreens = useCallback(
    (updatedTargetGroups: TargetGroup[], currentReduxScreens: any[]) => {
      const existingById = new Map(
        (currentReduxScreens || []).map((s: any) => [s.id, s])
      );

      const selectedScreens: any[] = [];

      updatedTargetGroups?.forEach(
        (targetGroup: TargetGroup, tgIndex: number) => {
          targetGroup.selectedArea?.forEach(
            (screen: any, screenIndex: number) => {
              const existing = existingById.get(screen.id);

              let mergedSlots = screen.slots || [];
              if (existing?.slots && existing.slots.length > 0) {
                mergedSlots = mergedSlots.map((slot: any, idx: number) => {
                  const existingSlot = existing.slots[idx];
                  if (
                    existingSlot?.creatives &&
                    existingSlot.creatives.length > 0
                  ) {
                    return { ...slot, creatives: existingSlot.creatives };
                  }
                  return slot;
                });
              }

              selectedScreens.push({
                id: screen.id,
                name: screen.name,
                price: screen.price,
                address: screen.address || 'N/A',
                deviceCode: screen.deviceCode || '',
                resolution: screen.resolution || screen.size,
                adUnit: screen.adUnit || '',
                partnerId: screen.partnerId || '',
                adUnitId: screen.adUnitId || '',
                numberOfSlots: screen.numberOfSlots || 1,
                slotTimeInSec: screen.slotTimeInSec || 0,
                numberOfBookedSlots: screen.consumedSlots || 1,
                slots: mergedSlots,
                sov:
                  screen.sov ||
                  (screen.numberOfSlots === 1
                    ? 100
                    : Math.floor(100 / (screen.numberOfSlots || 1))),
                targetGroupTitle: targetGroup.title || 'Unknown',
                targetGroupIndex: tgIndex,
                screenIndex,
                touchpoint: screen.touchpoint,
                deviceImage: screen.deviceImage,
                screenSize: screen.screenSize,
                geoCoordinates: screen.geoCoordinates,
                screenOnTime: screen.screenOnTime,
                screenOnDays: screen.screenOnDays,
              });
            }
          );
        }
      );

      formik.setField('selectedScreens', selectedScreens);
    },
    [formik.setField]
  );

  const handleSlotChange = useCallback(
    (
      targetGroupIndex: number,
      screenIndex: number,
      newConsumedSlots: number
    ) => {
      const updatedTargetGroups = [...(targetGroups || [])];
      const targetGroup = { ...updatedTargetGroups[targetGroupIndex] };
      const selectedArea = [...(targetGroup.selectedArea || [])];

      // Create a deep copy of the screen object to avoid extensibility issues
      const screen = JSON.parse(JSON.stringify(selectedArea[screenIndex]));

      const numberOfSlots = screen.numberOfSlots || 1;
      const occupiedSlots = screen.occupiedSlotCount || 0;
      const maxAvailableSlots = Math.max(0, numberOfSlots - occupiedSlots);

      if (newConsumedSlots === maxAvailableSlots) {
        dispatch(
          handleSnackbar({
            open: true,
            message: `Maximum limit reached: ${maxAvailableSlots} available slot${maxAvailableSlots !== 1 ? 's' : ''}`,
            severity: 'info',
            autoHideDuration: 3000,
          })
        );
      }

      // Clamp consumed slots between 1 and maxAvailableSlots
      const clampedSlots = Math.max(
        1,
        Math.min(maxAvailableSlots, newConsumedSlots)
      );

      // Calculate SOV percentage based on consumed slots
      const newSov = Math.round((clampedSlots / numberOfSlots) * 100);

      // Initialize or update slots array based on consumed slots
      const currentSlots = screen.slots || [];
      let updatedSlots = [...currentSlots];

      if (clampedSlots > currentSlots.length) {
        // Add new slots
        for (let i = currentSlots.length; i < clampedSlots; i++) {
          updatedSlots.push({
            name: `Slot ${i + 1}`,
            creatives: [],
          });
        }
      } else if (clampedSlots < currentSlots.length) {
        // Remove excess slots
        updatedSlots = updatedSlots.slice(0, clampedSlots);
      }

      // Create a new screen object with all properties
      const updatedScreen = {
        ...screen,
        consumedSlots: clampedSlots,
        sov: newSov,
        slots: updatedSlots,
      };

      selectedArea[screenIndex] = updatedScreen;
      targetGroup.selectedArea = selectedArea;
      updatedTargetGroups[targetGroupIndex] = targetGroup;

      // Update targetGroups in formik
      formik.setField('targetGroups', updatedTargetGroups);

      // Update selectedScreens field with all screen data
      updateSelectedScreens(updatedTargetGroups, reduxSelectedScreens);
    },
    [targetGroups, formik, updateSelectedScreens, reduxSelectedScreens]
  );

  const handleIncrement = useCallback(
    (
      targetGroupIndex: number,
      screenIndex: number,
      currentConsumedSlots: number
    ) => {
      handleSlotChange(targetGroupIndex, screenIndex, currentConsumedSlots + 1);
    },
    [handleSlotChange]
  );

  const handleDecrement = useCallback(
    (
      targetGroupIndex: number,
      screenIndex: number,
      currentConsumedSlots: number
    ) => {
      handleSlotChange(targetGroupIndex, screenIndex, currentConsumedSlots - 1);
    },
    [handleSlotChange]
  );

  const columns = useMemo(
    () => [
      { id: 'screenImage', label: 'Screen' },
      { id: 'name', label: 'Screen Name' },
      { id: 'price', label: 'Price per Slot' },
      { id: 'resolution', label: 'Resolution' },
      { id: 'aspectRatio', label: 'Aspect Ratio' },
      { id: 'screenSize', label: 'Screen Size' },
      { id: 'slots', label: 'Slots' },
      { id: 'occupiedSlots', label: 'Occupied Slots' },
      { id: 'slotsDuration', label: 'Slot Duration' },
      { id: 'loopTime', label: 'Loop Time' },
      { id: 'actions', label: 'SOV %' },
    ],
    []
  );

  const totalCost = useMemo(() => {
    // Calculate number of days between campaign start and end date
    const startDate = formik.values.campaignStartDate;
    const endDate = formik.values.campaignEndDate;

    let numberOfDays = 0;
    if (startDate && endDate) {
      // DateRangePicker returns dates in 'YYYY-MM-DD' format as strings
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Validate dates
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        // Calculate difference in days (inclusive of both start and end dates)
        const timeDiff = end.getTime() - start.getTime();
        numberOfDays = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) + 1;
        // Ensure at least 1 day
        numberOfDays = Math.max(1, numberOfDays);
      }
    }

    return allScreens.reduce((sum, screen) => {
      // Calculate: (price / 30) * numberOfDays * numberOfBookedSlots
      const dailyPrice = screen.price / 30;
      const screenCost = dailyPrice * numberOfDays * screen.numberOfBookedSlots;
      return sum + screenCost;
    }, 0);
  }, [
    allScreens,
    formik.values.campaignStartDate,
    formik.values.campaignEndDate,
  ]);

  const isOverBudget = useMemo(
    () => totalCost > (formik.values.campaignBudget || 0),
    [totalCost, formik.values.campaignBudget]
  );

  // Initialize selectedScreens field when component mounts or targetGroups change
  useEffect(() => {
    if (targetGroups && targetGroups.length > 0) {
      updateSelectedScreens(targetGroups, reduxSelectedScreens);
    }
  }, [targetGroups]);

  // Initialize slots for all screens when component mounts (only once)
  useEffect(() => {
    if (targetGroups && targetGroups.length > 0 && !isInitialized.current) {
      const updatedTargetGroups = JSON.parse(
        JSON.stringify(targetGroups || [])
      );
      let hasChanges = false;

      updatedTargetGroups.forEach((targetGroup: TargetGroup) => {
        targetGroup.selectedArea?.forEach((screen: any) => {
          if (!screen.slots || screen.slots.length === 0) {
            const consumedSlots = screen.consumedSlots || 1;
            screen.slots = Array.from(
              { length: consumedSlots },
              (_, index) => ({
                name: `Slot ${index + 1}`,
                creatives: [],
              })
            );
            hasChanges = true;
          }
        });
      });

      if (hasChanges) {
        formik.setField('targetGroups', updatedTargetGroups);
        updateSelectedScreens(updatedTargetGroups, reduxSelectedScreens);
      }

      isInitialized.current = true;
    }
  }, [targetGroups]);

  // Calculate total audience from unique touchpoints
  const totalAudience = useMemo(() => {
    if (!targetGroups || targetGroups.length === 0) return 0;

    // Track unique touchpoints to avoid counting the same touchpoint multiple times
    const uniqueTouchpoints = new Map<string, number>();

    targetGroups.forEach((targetGroup: TargetGroup) => {
      targetGroup.selectedArea?.forEach((screen: any) => {
        const touchpointId = screen.touchpoint?.id;
        const touchpointAudience =
          screen.touchpoint?.total_audience ||
          screen.touchpoint?.est_impressions ||
          0;

        // Only add if we haven't seen this touchpoint before
        if (touchpointId && !uniqueTouchpoints.has(touchpointId)) {
          uniqueTouchpoints.set(touchpointId, touchpointAudience);
        }
      });
    });

    // Sum all unique touchpoint audiences
    return Array.from(uniqueTouchpoints.values()).reduce(
      (sum, audience) => sum + audience,
      0
    );
  }, [targetGroups]);

  // Calculate target audience from unique touchpoints with filters
  const targetAudience = useMemo(() => {
    if (!targetGroups || targetGroups.length === 0) return 0;

    // Track unique touchpoints with their full data
    const uniqueTouchpoints = new Map<string, any>();

    targetGroups.forEach((targetGroup: TargetGroup) => {
      targetGroup.selectedArea?.forEach((screen: any) => {
        const touchpointId = screen.touchpoint?.id;

        // Only add if we haven't seen this touchpoint before
        if (touchpointId && !uniqueTouchpoints.has(touchpointId)) {
          uniqueTouchpoints.set(touchpointId, screen.touchpoint);
        }
      });
    });

    // Calculate target audience for each unique touchpoint and sum
    let total = 0;
    uniqueTouchpoints.forEach(touchpoint => {
      const touchpointTotalAudience =
        touchpoint.total_audience || touchpoint.est_impressions || 0;
      const touchpointTargetAudience = calculateTargetAudience(
        touchpointTotalAudience,
        touchpoint,
        targetGroupFilters
      );
      total += touchpointTargetAudience;
    });
    return total;
  }, [targetGroups, targetGroupFilters]);

  // Save audience metrics to formik whenever they change
  // Use refs to avoid infinite loops
  const prevAudienceRef = useRef({ total: 0, target: 0 });

  useEffect(() => {
    if (
      prevAudienceRef.current.total !== totalAudience ||
      prevAudienceRef.current.target !== targetAudience
    ) {
      formik.setField('totalAudience', totalAudience);
      formik.setField('targetAudience', targetAudience);
      prevAudienceRef.current = {
        total: totalAudience,
        target: targetAudience,
      };
    }
  }, [totalAudience, targetAudience]);

  // Create accordion items grouped by touchpoint
  const accordionItems = useMemo(() => {
    if (!targetGroups || targetGroups.length === 0) return [];

    // Group screens by touchpoint across all target groups
    const screensByTouchpoint: Record<
      string,
      { screens: any[]; tgIndices: number[] }
    > = {};

    targetGroups.forEach((targetGroup: TargetGroup, tgIndex: number) => {
      targetGroup.selectedArea?.forEach((screen: any, screenIndex: number) => {
        const touchpointName = screen.touchpoint?.name || 'Unknown Touchpoint';

        if (!screensByTouchpoint[touchpointName]) {
          screensByTouchpoint[touchpointName] = { screens: [], tgIndices: [] };
        }

        screensByTouchpoint[touchpointName].screens.push({
          ...screen,
          targetGroupIndex: tgIndex,
          screenIndex,
        });

        if (!screensByTouchpoint[touchpointName].tgIndices.includes(tgIndex)) {
          screensByTouchpoint[touchpointName].tgIndices.push(tgIndex);
        }
      });
    });

    // Create accordion items for each touchpoint
    return Object.entries(screensByTouchpoint).map(([touchpointName, data]) => {
      const screensCount = data.screens.length;
      const title = `${touchpointName} (${screensCount} screen${screensCount !== 1 ? 's' : ''})`;

      // Create table rows for this touchpoint's screens
      const tableRows = data.screens.map((screen: any) => {
        const numberOfSlots = screen.numberOfSlots || 1;
        const consumedSlots = screen.consumedSlots || 1;
        const sov =
          screen.sov ||
          (numberOfSlots === 1 ? 100 : Math.floor(100 / numberOfSlots));
        const slotTimeInSec = screen.slotTimeInSec || 0;
        const loopTimeInSec = numberOfSlots * slotTimeInSec;
        return {
          id: screen.id,
          screenImage: (
            <Box
              sx={{
                position: 'relative',
                width: 120,
                height: 80,
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                '&:hover .eye-icon': {
                  opacity: 1,
                },
              }}
            >
              <img
                src={screen.deviceImage?.[0]?.url || DEFAULT_SCREEN_IMAGE}
                alt={screen.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
              <IconButton
                className="eye-icon"
                onClick={() =>
                  dispatch(
                    setImagePreviewModal(
                      screen.deviceImage?.[0]?.url || DEFAULT_SCREEN_IMAGE
                    )
                  )
                }
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'transparent',
                  color: '#1a0303ff',
                  opacity: 0,
                  transition: 'opacity 0.3s',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <VisibilityIcon />
              </IconButton>
            </Box>
          ),
          name: screen.name,
          price: `₹ ${Math.floor(screen.price).toLocaleString('en-IN')} / slot`,
          resolution: screen.resolution
            ? `${screen.resolution.width} * ${screen.resolution.height}`
            : 'N/A',
          aspectRatio: calculateAspectRatio(screen.resolution),
          screenSize: `${screen.screenSize} inches`,
          slots: `${consumedSlots}/${numberOfSlots}`,
          occupiedSlots: screen.occupiedSlotCount || 0,
          slotsDuration: `${slotTimeInSec} s`,
          loopTime: formatLoopTime(loopTimeInSec),
          sov: `${sov}%`,
          actions: (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: '#2e7d32',
                borderRadius: '20px',
                padding: '4px 8px',
                minWidth: '80px',
                justifyContent: 'space-between',
              }}
            >
              <IconButton
                size="small"
                onClick={() =>
                  handleDecrement(
                    screen.targetGroupIndex,
                    screen.screenIndex,
                    consumedSlots
                  )
                }
                disabled={consumedSlots <= 1}
                sx={{
                  color: 'white',
                  padding: '2px',
                  minWidth: '24px',
                  width: '24px',
                  height: '24px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&:disabled': {
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  minWidth: '20px',
                  textAlign: 'center',
                  fontSize: '14px',
                }}
              >
                {sov}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  handleIncrement(
                    screen.targetGroupIndex,
                    screen.screenIndex,
                    consumedSlots
                  )
                }
                disabled={
                  consumedSlots >=
                  (screen.numberOfSlots || 1) - (screen.occupiedSlotCount || 0)
                }
                sx={{
                  color: 'white',
                  padding: '2px',
                  minWidth: '24px',
                  width: '24px',
                  height: '24px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&:disabled': {
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          ),
        };
      });

      return {
        id: `touchpoint-${touchpointName}`,
        title,
        defaultExpanded: true,
        content: (
          <Box sx={{ p: 2 }}>
            {tableRows.length > 0 ? (
              <CustomTable
                columns={columns}
                rows={tableRows}
                page={0}
                rowsPerPage={tableRows.length}
                totalItems={tableRows.length}
                paginationPosition="none"
                disableAction={false}
              />
            ) : (
              <Typography
                variant="body1"
                color="text.secondary"
                textAlign="center"
                py={4}
              >
                No screens selected for this touchpoint.
              </Typography>
            )}
          </Box>
        ),
      };
    });
  }, [targetGroups, columns, formatLoopTime, handleDecrement, handleIncrement]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1500, mx: 'auto', p: 2 }}>
      <Stack spacing={3}>
        {/* Summary Stats */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          {/* Campaign Budget */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" color="text.primary">
              Campaign Budget:
            </Typography>
            <Chip
              label={`₹${formik.values.campaignBudget?.toLocaleString() || 0}`}
              color="primary"
              variant="filled"
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                height: '32px',
                borderRadius: '16px',
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Audience:
                </Typography>
                <Chip
                  label={formatNumberCompact(totalAudience)}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              {/* Only show Target Audience if filters are selected */}
              {filtersSelected && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Target Audience:
                  </Typography>
                  <Chip
                    label={formatNumberCompact(targetAudience)}
                    color="success"
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Total Cost */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" color="text.primary">
              Total Cost:
            </Typography>
            <Chip
              label={`₹${Math.floor(totalCost).toLocaleString('en-IN')}`}
              color={isOverBudget ? 'warning' : 'success'}
              variant="filled"
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                height: '32px',
                borderRadius: '16px',
                minWidth: '120px',
                justifyContent: 'center',
              }}
            />
            <Tooltip title="Available in Premium" arrow placement="top">
              <IconButton
                size="small"
                sx={{
                  color: 'text.disabled',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Multiple Accordions - One per Touchpoint */}
        {targetGroups && targetGroups.length > 0 ? (
          <AccordionGroup
            items={accordionItems}
            allowMultiple={true}
            spacing={2}
          />
        ) : (
          <Typography
            variant="body1"
            color="text.secondary"
            textAlign="center"
            py={4}
          >
            No target groups found. Please go back to Screen Selection step to
            select screens.
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default memo(ScreenSov);
